<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { ImportDomainsResponse } from '@/types'

const props = defineProps<{
  visible: boolean
  results: ImportDomainsResponse | null
}>()

const emit = defineEmits<{
  close: []
  'import-more': []
}>()

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { t } = useI18n()
const router = useRouter()

// Get success/failure counts
const summary = computed(() => {
  if (!props.results) {
    return { total: 0, successful: 0, failed: 0 }
  }
  return props.results.summary
})

// Navigate to a domain's detail page
const viewDomain = (domainId: number) => {
  router.push({ name: 'dashboardDomainManagement', params: { id: domainId } })
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="visible && results" class="results-overlay" @click.self="emit('close')">
        <div class="results-dialog">
          <!-- Header -->
          <header class="dialog-header">
            <div class="header-icon" :class="summary.failed === 0 ? 'success' : 'warning'">
              <v-icon
                :icon="summary.failed === 0 ? 'mdi-check-circle' : 'mdi-alert-circle'"
                size="32"
              />
            </div>
            <div class="header-text">
              <h2 class="dialog-title">{{ $t('dashboard.admin.migration.results.title') }}</h2>
              <p class="dialog-subtitle">{{ $t('dashboard.admin.migration.results.subtitle') }}</p>
            </div>
            <button class="close-btn" @click="emit('close')">
              <v-icon icon="mdi-close" size="20" />
            </button>
          </header>

          <!-- Summary Stats -->
          <div class="summary-section">
            <h3 class="section-title">{{ $t('dashboard.admin.migration.results.summary') }}</h3>
            <div class="summary-stats">
              <div class="stat-box">
                <span class="stat-value">{{ summary.total }}</span>
                <span class="stat-label">{{ $t('dashboard.admin.migration.results.total') }}</span>
              </div>
              <div class="stat-box success">
                <span class="stat-value">{{ summary.successful }}</span>
                <span class="stat-label">{{ $t('dashboard.admin.migration.results.successful') }}</span>
              </div>
              <div class="stat-box" :class="{ error: summary.failed > 0 }">
                <span class="stat-value">{{ summary.failed }}</span>
                <span class="stat-label">{{ $t('dashboard.admin.migration.results.failed') }}</span>
              </div>
            </div>
          </div>

          <!-- Results List -->
          <div class="results-section">
            <h3 class="section-title">{{ $t('dashboard.admin.migration.results.domainResults') }}</h3>
            <div class="results-list">
              <div
                v-for="result in results.results"
                :key="result.domain"
                class="result-item"
                :class="{ success: result.success, error: !result.success }"
              >
                <div class="result-info">
                  <span class="domain-name">{{ result.domain }}</span>
                  <span v-if="result.success" class="result-status success">
                    <v-icon icon="mdi-check" size="14" />
                    {{ $t('dashboard.admin.migration.results.success') }}
                  </span>
                  <span v-else class="result-status error">
                    <v-icon icon="mdi-close" size="14" />
                    {{ result.error || $t('dashboard.admin.migration.results.error') }}
                  </span>
                </div>
                <button
                  v-if="result.success && result.result?.domain_id"
                  class="view-btn"
                  @click="viewDomain(result.result.domain_id)"
                >
                  <v-icon icon="mdi-open-in-new" size="16" />
                  <span>{{ $t('dashboard.admin.migration.results.viewDomain') }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <footer class="dialog-footer">
            <button class="secondary-btn" @click="emit('import-more')">
              <v-icon icon="mdi-plus" size="18" />
              <span>{{ $t('dashboard.admin.migration.results.importMore') }}</span>
            </button>
            <button class="primary-btn" @click="emit('close')">
              {{ $t('dashboard.admin.migration.results.close') }}
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.results-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

.results-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 560px;
  max-height: 80vh;
  background: linear-gradient(135deg, rgba(15, 23, 48, 0.98), rgba(10, 15, 35, 0.98));
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

/* Header */
.dialog-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  flex-shrink: 0;

  &.success {
    background: rgba(76, 175, 80, 0.15);
    color: #81c784;
  }

  &.warning {
    background: rgba(255, 193, 7, 0.15);
    color: #ffd54f;
  }
}

.header-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dialog-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.dialog-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.6);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(244, 67, 54, 0.1);
    border-color: rgba(244, 67, 54, 0.3);
    color: #e57373;
  }
}

/* Summary Section */
.summary-section {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.section-title {
  margin: 0 0 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(200, 218, 255, 0.5);
}

.summary-stats {
  display: flex;
  gap: 12px;
}

.stat-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 14px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;

  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
  }

  .stat-label {
    font-size: 0.75rem;
    color: rgba(200, 218, 255, 0.5);
  }

  &.success {
    background: rgba(76, 175, 80, 0.08);
    border-color: rgba(76, 175, 80, 0.2);

    .stat-value {
      color: #81c784;
    }
  }

  &.error {
    background: rgba(244, 67, 54, 0.08);
    border-color: rgba(244, 67, 54, 0.2);

    .stat-value {
      color: #e57373;
    }
  }
}

/* Results Section */
.results-section {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;

  &.success {
    border-color: rgba(76, 175, 80, 0.2);
  }

  &.error {
    border-color: rgba(244, 67, 54, 0.2);
    background: rgba(244, 67, 54, 0.05);
  }
}

.result-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.domain-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.result-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;

  &.success {
    color: #81c784;
  }

  &.error {
    color: #e57373;
  }
}

.view-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 6px;
  color: #42a5f5;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(33, 150, 243, 0.15);
  }
}

/* Footer */
.dialog-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.secondary-btn,
.primary-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.secondary-btn {
  background: transparent;
  border: 1px solid rgba(120, 163, 255, 0.2);
  color: rgba(200, 218, 255, 0.7);

  &:hover {
    background: rgba(120, 163, 255, 0.05);
  }
}

.primary-btn {
  background: rgba(0, 150, 136, 0.15);
  border: 1px solid rgba(0, 150, 136, 0.3);
  color: #26a69a;

  &:hover {
    background: rgba(0, 150, 136, 0.25);
  }
}

/* Fade Animation */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;

  .results-dialog {
    transform: scale(0.95);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .results-dialog {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 255, 0.98));
  }

  .dialog-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .dialog-subtitle {
    color: rgba(15, 23, 42, 0.6);
  }

  .domain-name {
    color: rgba(15, 23, 42, 0.9);
  }
}

/* Responsive */
@media (max-width: 480px) {
  .results-overlay {
    padding: 16px;
  }

  .summary-stats {
    flex-direction: column;
  }

  .dialog-footer {
    flex-direction: column;
  }
}
</style>
